/**
 * Locate links: one URL that lands on a floor and an object, in either
 * Overview view (twin slice 1, W4; frozen in C0's contract note §3).
 *
 *   /overview?site=<site>[&view=2d|3d][&level=<L>][&focus=<kind>:<id>][&from=<origin>]
 *
 * A link only POINTS. It never carries a camera pose and never opens a
 * drawer; `level` is a hint, and an absent `view` means the device's own
 * remembered choice. What the Overview does on landing is not this
 * module's business -- it builds and reads the bytes.
 *
 * THE BYTES ARE THE CONTRACT. Two builders make these links: this one
 * (toasts, drawers, "Show where") and the api-server's
 * `notifications.console_links.locate_path` (the Chat "Where:" line). Both
 * are held to the same table, `tests/fixtures/locate-links.json`, and
 * never to each other, so a divergence turns exactly one side red. The
 * rules:
 *
 * - parameters always come out in the order site, view, level, focus,
 *   from, whatever order the caller holds them in;
 * - an absent (null) or empty value is left out entirely;
 * - every value, `site` included, is escaped exactly as Python's
 *   `urllib.parse.quote(value, safe=':/')` escapes it. `encodeURIComponent`
 *   is close but not equal: it leaves `!'()*` alone and escapes `:` and
 *   `/`, so it is NOT used for the ASCII range here;
 * - an empty or missing site, or a view other than `2d`/`3d`, is refused
 *   with a throw, never turned into a link.
 *
 * The builder deliberately does not police the grammar of `focus` and
 * `from`: the server's builder does not either, and two builders that
 * refuse different inputs are two contracts. Callers holding a typed
 * value use `formatFocus` / `formatFrom`, which can only produce the
 * grammar.
 */

export const LOCATE_ROUTE = '/overview';

export const LOCATE_PARAM_ORDER = ['site', 'view', 'level', 'focus', 'from'] as const;
export type LocateParamName = (typeof LOCATE_PARAM_ORDER)[number];

export type LocateView = '2d' | '3d';
export const LOCATE_VIEWS: readonly LocateView[] = ['2d', '3d'];

/**
 * A link's parameters, by the URL's own names -- so `from` is the key,
 * where the Python builder has to spell it `from_`.
 */
export interface LocateArgs {
  site: string | null | undefined;
  view?: string | null;
  level?: string | null;
  focus?: string | null;
  from?: string | null;
}

/** Thrown for a link that must never be built. */
export class LocateLinkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LocateLinkError';
  }
}

/** Python's always-safe set (`A-Za-z0-9_.-~`) plus the contract's `:/`. */
const SAFE_CHAR = /^[A-Za-z0-9_.\-~:/]$/;

/**
 * `urllib.parse.quote(value, safe=':/')`, byte for byte.
 *
 * ASCII outside the safe set becomes `%XX` with upper-case hex. Anything
 * else is UTF-8, which `encodeURIComponent` produces with upper-case hex
 * too. A lone surrogate has no UTF-8 form: `encodeURIComponent` throws
 * `URIError` for it, just as Python's `quote` raises `UnicodeEncodeError`,
 * so both builders refuse it rather than inventing bytes.
 */
export function quoteLocateValue(value: string): string {
  let out = '';
  // for...of walks code points, so a surrogate PAIR arrives whole.
  for (const ch of value) {
    if (SAFE_CHAR.test(ch)) {
      out += ch;
      continue;
    }
    const code = ch.codePointAt(0) ?? 0;
    out +=
      code < 0x80 ? `%${code.toString(16).toUpperCase().padStart(2, '0')}` : encodeURIComponent(ch);
  }
  return out;
}

/** The path (with query) that locates `args.focus` at `args.site`. */
export function locateHref(args: LocateArgs): string {
  const { site, view } = args;
  if (!site) throw new LocateLinkError('a locate link needs a site');
  if (view && !(LOCATE_VIEWS as readonly string[]).includes(view)) {
    throw new LocateLinkError(`a locate link's view is 2d or 3d, not ${JSON.stringify(view)}`);
  }
  const query = LOCATE_PARAM_ORDER.flatMap((name) => {
    const value = args[name];
    return value ? [`${name}=${quoteLocateValue(value)}`] : [];
  }).join('&');
  return `${LOCATE_ROUTE}?${query}`;
}

// ---------------------------------------------------------------------------
// Focus and from: the typed side of the grammar
// ---------------------------------------------------------------------------

/**
 * What a link points at. A robot is ALWAYS fleet-qualified in a link,
 * with `robotName` kept bare -- the bare name is what every drawer,
 * selection and robot list matches on.
 */
export type LocateFocus =
  | { kind: 'alert'; alertId: string }
  | { kind: 'robot'; fleetName: string; robotName: string }
  | { kind: 'lift'; liftName: string }
  | { kind: 'door'; doorName: string }
  | { kind: 'zone'; place: string }
  | { kind: 'task'; taskId: string }
  | { kind: 'level'; levelName: string };

export type LocateFocusKind = LocateFocus['kind'];

/** Where the person came from, for the Back chip. */
export type LocateFrom =
  | { kind: 'alert'; alertId: string }
  | { kind: 'task'; taskId: string }
  | { kind: 'chat' | 'drawer' | 'attention' | 'shared' };

/**
 * `<fleet>/<robot>` -> both halves, split at the FIRST slash; a value
 * with no slash -> the bare name with no fleet. Null when either half
 * would be empty. Shared with the alert deep-link parser, which still has
 * to accept a bare name (robot alerts carry one today).
 */
export function splitRobotRef(value: string): { fleetName?: string; robotName: string } | null {
  const slash = value.indexOf('/');
  if (slash < 0) return value ? { robotName: value } : null;
  const fleetName = value.slice(0, slash);
  const robotName = value.slice(slash + 1);
  if (!fleetName || !robotName) return null;
  return { fleetName, robotName };
}

function splitKind(text: string): [string, string] | null {
  const colon = text.indexOf(':');
  // An empty kind needs no check of its own: no kind is spelled ''.
  if (colon < 0) return null;
  const id = text.slice(colon + 1);
  return id ? [text.slice(0, colon), id] : null;
}

/** `kind:id` -> a typed focus; null for anything outside the grammar. */
export function parseFocus(text: string | null | undefined): LocateFocus | null {
  if (!text) return null;
  const parts = splitKind(text);
  if (!parts) return null;
  const [kind, id] = parts;
  switch (kind) {
    case 'alert':
      return { kind: 'alert', alertId: id };
    case 'robot': {
      const ref = splitRobotRef(id);
      // A link never carries a bare robot name: two fleets may hold the
      // same one, and a link must not pick for the reader.
      if (!ref?.fleetName) return null;
      return { kind: 'robot', fleetName: ref.fleetName, robotName: ref.robotName };
    }
    case 'lift':
      return { kind: 'lift', liftName: id };
    case 'door':
      return { kind: 'door', doorName: id };
    case 'zone':
      return { kind: 'zone', place: id };
    case 'task':
      return { kind: 'task', taskId: id };
    case 'level':
      return { kind: 'level', levelName: id };
    default:
      return null;
  }
}

/** The inverse of `parseFocus`: the `focus=` value, unescaped. */
export function formatFocus(focus: LocateFocus): string {
  switch (focus.kind) {
    case 'alert':
      return `alert:${focus.alertId}`;
    case 'robot':
      return `robot:${focus.fleetName}/${focus.robotName}`;
    case 'lift':
      return `lift:${focus.liftName}`;
    case 'door':
      return `door:${focus.doorName}`;
    case 'zone':
      return `zone:${focus.place}`;
    case 'task':
      return `task:${focus.taskId}`;
    case 'level':
      return `level:${focus.levelName}`;
  }
}

const BARE_FROM = new Set(['chat', 'drawer', 'attention', 'shared']);

/** A `from=` value -> typed; null for anything outside the grammar. */
export function parseFrom(text: string | null | undefined): LocateFrom | null {
  if (!text) return null;
  if (BARE_FROM.has(text)) return { kind: text as 'chat' | 'drawer' | 'attention' | 'shared' };
  const parts = splitKind(text);
  if (!parts) return null;
  const [kind, id] = parts;
  if (kind === 'alert') return { kind: 'alert', alertId: id };
  if (kind === 'task') return { kind: 'task', taskId: id };
  return null;
}

/** The inverse of `parseFrom`. */
export function formatFrom(from: LocateFrom): string {
  switch (from.kind) {
    case 'alert':
      return `alert:${from.alertId}`;
    case 'task':
      return `task:${from.taskId}`;
    default:
      return from.kind;
  }
}

// ---------------------------------------------------------------------------
// Reading a link
// ---------------------------------------------------------------------------

/**
 * A link as the Overview reads it. Lenient where the builder is strict: a
 * hand-edited or truncated URL must never crash the page, so a bad value
 * reads as absent instead, and the page says what it could not follow.
 */
export interface ParsedLocate {
  site: string | null;
  /** Null when absent OR not `2d`/`3d`: either way the device's choice applies. */
  view: LocateView | null;
  /** A hint, as written. Compare it with `sameLevel`, never `===`. */
  level: string | null;
  focus: LocateFocus | null;
  /**
   * The `focus=` value as written, kept even when it does not parse, so
   * the page can name the thing it could not find instead of silently
   * showing nothing.
   */
  focusRaw: string | null;
  from: LocateFrom | null;
}

function toParams(search: string | URLSearchParams): URLSearchParams {
  return typeof search === 'string' ? new URLSearchParams(search) : search;
}

function nonEmpty(params: URLSearchParams, name: LocateParamName): string | null {
  const value = params.get(name);
  return value ? value : null;
}

/**
 * Read a location's search string (`?site=…`, with or without the `?`).
 *
 * Decoding is `URLSearchParams`'s, so a literal `+` reads as a space, as
 * a form would send it. The builder never emits one (it writes `%2B`), so
 * this only matters for a link typed by hand.
 */
export function parseLocate(search: string | URLSearchParams): ParsedLocate {
  const params = toParams(search);
  const view = nonEmpty(params, 'view');
  const focusRaw = nonEmpty(params, 'focus');
  return {
    site: nonEmpty(params, 'site'),
    view: view && (LOCATE_VIEWS as readonly string[]).includes(view) ? (view as LocateView) : null,
    level: nonEmpty(params, 'level'),
    focus: parseFocus(focusRaw),
    focusRaw,
    from: parseFrom(nonEmpty(params, 'from')),
  };
}

/** True when the search carries any non-empty locate parameter. */
export function hasLocateParams(search: string | URLSearchParams): boolean {
  const params = toParams(search);
  return LOCATE_PARAM_ORDER.some((name) => Boolean(params.get(name)));
}
